/**
 * Multi-Room WebSocket Manager
 * Handles WebSocket connections for multiple game rooms simultaneously
 */

import * as Colyseus from 'colyseus.js';
import config from '../config/environment';
import type { GameState } from '../types/game';
import { dbg } from '../utils/debugLogger';

export interface RoomConnection {
  roomId: string;
  room: Colyseus.Room;
  isConnected: boolean;
  lastActivity: Date;
  connectionAttempts: number;
  maxReconnectAttempts: number;
}

export interface MultiRoomWebSocketManager {
  connections: Map<string, RoomConnection>;
  client: Colyseus.Client;
  maxConnections: number;
  onRoomUpdate: (roomId: string, state: any) => void;
  onRoomError: (roomId: string, error: string) => void;
  onPlayerAction: (roomId: string, action: string, data: any) => void;
}

class MultiRoomWebSocketService {
  private client: Colyseus.Client;
  private connections: Map<string, RoomConnection> = new Map();
  private maxConnections: number = 5;
  private reconnectDelay: number = 3000;
  
  // Event callbacks
  private onRoomUpdate?: (roomId: string, state: any) => void;
  private onRoomError?: (roomId: string, error: string) => void;
  private onPlayerAction?: (roomId: string, action: string, data: any) => void;
  private onConnectionChange?: (roomId: string, isConnected: boolean) => void;

  constructor() {
    this.client = new Colyseus.Client(config.api.wsUrl || 'ws://localhost:2567');
  }

  /**
   * Set event callbacks
   */
  setEventHandlers(handlers: {
    onRoomUpdate?: (roomId: string, state: any) => void;
    onRoomError?: (roomId: string, error: string) => void;
    onPlayerAction?: (roomId: string, action: string, data: any) => void;
    onConnectionChange?: (roomId: string, isConnected: boolean) => void;
  }) {
    this.onRoomUpdate = handlers.onRoomUpdate;
    this.onRoomError = handlers.onRoomError;
    this.onPlayerAction = handlers.onPlayerAction;
    this.onConnectionChange = handlers.onConnectionChange;
  }

  /**
   * Connect to a room
   */
  async connectToRoom(roomId: string, username: string, gameType: string = 'kadi-ni-tidi'): Promise<boolean> {
    try {
      // Check if already connected
      if (this.connections.has(roomId)) {
        const connection = this.connections.get(roomId);
        if (connection?.isConnected) {
          dbg('network', `Already connected to room ${roomId}`);
          return true;
        }
      }

      // Check connection limit
      const activeConnections = Array.from(this.connections.values()).filter(conn => conn.isConnected);
      if (activeConnections.length >= this.maxConnections) {
        throw new Error('Maximum room connections reached');
      }

  dbg('network', `Connecting to room ${roomId} with username ${username}`);
      
      // Attempt to join the room
      const room = await this.client.joinOrCreate(gameType, {
        roomId,
        username,
        joinMode: 'multi-room'
      });

      // Create connection object
      const connection: RoomConnection = {
        roomId,
        room,
        isConnected: true,
        lastActivity: new Date(),
        connectionAttempts: 1,
        maxReconnectAttempts: 3
      };

      // Set up room event handlers
      this.setupRoomHandlers(connection);

      // Store connection
      this.connections.set(roomId, connection);

      // Notify connection change
      this.onConnectionChange?.(roomId, true);

  dbg('network', `Successfully connected to room ${roomId}`);
      return true;

    } catch (error) {
      console.error(`Failed to connect to room ${roomId}:`, error);
      this.onRoomError?.(roomId, error instanceof Error ? error.message : 'Connection failed');
      return false;
    }
  }

  /**
   * Disconnect from a room
   */
  async disconnectFromRoom(roomId: string): Promise<void> {
    const connection = this.connections.get(roomId);
    if (!connection) {
      return;
    }

    try {
      if (connection.room && connection.isConnected) {
        await connection.room.leave();
      }
    } catch (error) {
      console.error(`Error leaving room ${roomId}:`, error);
    }

    // Update connection status
    connection.isConnected = false;
    
    // Notify connection change
    this.onConnectionChange?.(roomId, false);

    // Remove from connections
    this.connections.delete(roomId);
    
  dbg('network', `Disconnected from room ${roomId}`);
  }

  /**
   * Send action to a specific room
   */
  async sendRoomAction(roomId: string, action: string, data: any = {}): Promise<boolean> {
    const connection = this.connections.get(roomId);
    if (!connection?.isConnected || !connection.room) {
      console.warn(`Cannot send action to room ${roomId}: not connected`);
      return false;
    }

    try {
      connection.room.send(action, data);
      connection.lastActivity = new Date();
      return true;
    } catch (error) {
      console.error(`Failed to send action ${action} to room ${roomId}:`, error);
      this.onRoomError?.(roomId, `Failed to send action: ${action}`);
      return false;
    }
  }

  /**
   * Get room connection status
   */
  isRoomConnected(roomId: string): boolean {
    const connection = this.connections.get(roomId);
    return connection?.isConnected ?? false;
  }

  /**
   * Get all connected rooms
   */
  getConnectedRooms(): string[] {
    return Array.from(this.connections.entries())
      .filter(([_, connection]) => connection.isConnected)
      .map(([roomId, _]) => roomId);
  }

  /**
   * Get connection stats
   */
  getConnectionStats() {
    const connected = this.getConnectedRooms().length;
    return {
      connected,
      total: this.connections.size,
      maxConnections: this.maxConnections,
      canConnect: connected < this.maxConnections
    };
  }

  /**
   * Disconnect from all rooms
   */
  async disconnectAll(): Promise<void> {
    const roomIds = Array.from(this.connections.keys());
    await Promise.all(roomIds.map(roomId => this.disconnectFromRoom(roomId)));
  }

  /**
   * Set up event handlers for a room connection
   */
  private setupRoomHandlers(connection: RoomConnection): void {
    const { room, roomId } = connection;

    // State change handler
    room.onStateChange((state: GameState) => {
      connection.lastActivity = new Date();
      this.onRoomUpdate?.(roomId, state);
    });

    // Message handler for game events
    room.onMessage('*', (message: any) => {
      connection.lastActivity = new Date();
      this.onPlayerAction?.(roomId, message.type || 'unknown', message.data || message);
    });

    // Error handler
    room.onError((code: number, message?: string) => {
      console.error(`Room ${roomId} error:`, code, message);
      this.onRoomError?.(roomId, message || `Error code: ${code}`);
    });

    // Leave handler
    room.onLeave((code: number) => {
  dbg('network', `Left room ${roomId} with code:`, code);
      connection.isConnected = false;
      this.onConnectionChange?.(roomId, false);
      
      // Attempt reconnection if unexpected
      if (code !== 1000 && connection.connectionAttempts < connection.maxReconnectAttempts) {
        this.attemptReconnection(connection);
      }
    });
  }

  /**
   * Attempt to reconnect to a room
   */
  private async attemptReconnection(connection: RoomConnection): Promise<void> {
    const { roomId, connectionAttempts, maxReconnectAttempts } = connection;
    
    if (connectionAttempts >= maxReconnectAttempts) {
  dbg('network', `Max reconnection attempts reached for room ${roomId}`);
      this.connections.delete(roomId);
      return;
    }

  dbg('network', `Attempting to reconnect to room ${roomId} (attempt ${connectionAttempts + 1})`);
    
    // Wait before reconnecting
    await new Promise(resolve => setTimeout(resolve, this.reconnectDelay));
    
    try {
      // Try to rejoin the room
      const room = await this.client.reconnect(connection.room.reconnectionToken);
      
      // Update connection
      connection.room = room;
      connection.isConnected = true;
      connection.connectionAttempts++;
      connection.lastActivity = new Date();
      
      // Set up handlers again
      this.setupRoomHandlers(connection);
      
      // Notify reconnection
      this.onConnectionChange?.(roomId, true);
      
  dbg('network', `Successfully reconnected to room ${roomId}`);
      
    } catch (error) {
      console.error(`Reconnection failed for room ${roomId}:`, error);
      connection.connectionAttempts++;
      
      // Try again if under limit
      if (connection.connectionAttempts < maxReconnectAttempts) {
        this.attemptReconnection(connection);
      } else {
        this.connections.delete(roomId);
        this.onRoomError?.(roomId, 'Failed to reconnect after multiple attempts');
      }
    }
  }

  /**
   * Clean up inactive connections
   */
  cleanupInactiveConnections(): void {
    const now = new Date();
    const inactivityThreshold = 5 * 60 * 1000; // 5 minutes

    for (const [roomId, connection] of this.connections.entries()) {
      if (!connection.isConnected) {
        continue;
      }

      const inactiveTime = now.getTime() - connection.lastActivity.getTime();
    if (inactiveTime > inactivityThreshold) {
  dbg('network', `Cleaning up inactive connection to room ${roomId}`);
      this.disconnectFromRoom(roomId);
      }
    }
  }

  /**
   * Start periodic cleanup
   */
  startCleanupTimer(): void {
    setInterval(() => {
      this.cleanupInactiveConnections();
    }, 60000); // Check every minute
  }
}

// Export singleton instance
export const multiRoomWebSocket = new MultiRoomWebSocketService();
export default multiRoomWebSocket;